import { AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/button'

export interface ChangeDetectionBannerProps {
  additions: number
  deletions: number
  onViewInSource: () => void
  /** Clear markers without switching modes (external/auto edits the user has already seen). */
  onDismiss?: () => void
  className?: string
}

/**
 * Banner component that appears in visual mode when changes are detected.
 * Shows change statistics and provides a button to switch to code view (CodeMirror + gutter).
 */
export function ChangeDetectionBanner({
  additions,
  deletions,
  onViewInSource,
  onDismiss,
  className = ''
}: ChangeDetectionBannerProps) {
  return (
    <div className={`absolute top-0 left-0 right-0 z-10 h-11 border-b bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-900/30 ${className}`}>
      <div className="flex h-11 items-center justify-between gap-2 px-4">
        <div className="flex items-center gap-2 min-w-0">
          <AlertTriangle className="w-4 h-4 shrink-0 text-amber-600 dark:text-amber-400" />
          <span className="text-sm text-amber-900 dark:text-amber-100">
            External changes:{' '}
            <span className="text-green-600 dark:text-green-400 font-medium">+{additions}</span>
            {' '}
            <span className="text-red-600 dark:text-red-400 font-medium">-{deletions}</span>
            {' '}lines
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {onDismiss && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onDismiss}
              className="text-amber-900 dark:text-amber-100 hover:bg-amber-100 dark:hover:bg-amber-900/30"
            >
              Dismiss
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={onViewInSource}
            className="border-amber-300 dark:border-amber-700 text-amber-900 dark:text-amber-100 hover:bg-amber-100 dark:hover:bg-amber-900/30"
          >
            View in code
          </Button>
        </div>
      </div>
    </div>
  )
}

